import { AxiosError } from 'axios';
import { inspectionClient } from '@/config/api';
import {
  InspectionReportReview,
  ApproveReportRequest,
  RejectReportRequest,
  ReassignToInspectorRequest,
} from '@/shared/types/inspectionReportReview.types';

export class InspectionReportReviewService {
  /**
   * Aprovar um laudo (Prestadora ou Banco)
   */
  async approveReport(
    reportId: string,
    reviewerType: 'Provider' | 'Bank',
    payload: ApproveReportRequest
  ): Promise<InspectionReportReview> {
    const response = await inspectionClient.post<InspectionReportReview>(
      `/api/inspection-report-reviews/${reportId}/approve?reviewerType=${reviewerType}`,
      payload
    );
    return response.data;
  }

  /**
   * Rejeitar um laudo com correções (Prestadora ou Banco)
   */
  async rejectReport(
    reportId: string,
    reviewerType: 'Provider' | 'Bank',
    payload: RejectReportRequest
  ): Promise<InspectionReportReview> {
    const response = await inspectionClient.post<InspectionReportReview>(
      `/api/inspection-report-reviews/${reportId}/reject?reviewerType=${reviewerType}`,
      payload
    );
    return response.data;
  }

  /**
   * Prestadora repassar correção para o inspetor
   */
  async reassignToInspector(
    reportId: string,
    payload: ReassignToInspectorRequest
  ): Promise<void> {
    await inspectionClient.post(
      `/api/inspection-report-reviews/${reportId}/reassign-to-inspector`,
      payload
    );
  }

  /**
   * Obter todas as revisões de um laudo
   */
  async getReviewsByReportId(reportId: string): Promise<InspectionReportReview[]> {
    const response = await inspectionClient.get<InspectionReportReview[]>(
      `/api/inspection-report-reviews/report/${reportId}`
    );
    return response.data;
  }

  /**
   * Obter todas as revisões de todos os laudos de um InspectionItem
   * (útil para mostrar histórico completo mesmo quando laudos são deletados)
   */
  async getReviewsByInspectionItemId(inspectionItemId: string): Promise<InspectionReportReview[]> {
    const response = await inspectionClient.get<InspectionReportReview[]>(
      `/api/inspection-report-reviews/item/${inspectionItemId}`
    );
    return response.data;
  }

  /**
   * Obter a revisão mais recente de um laudo
   */
  async getLatestReview(reportId: string): Promise<InspectionReportReview | null> {
    try {
      const response = await inspectionClient.get<InspectionReportReview>(
        `/api/inspection-report-reviews/report/${reportId}/latest`
      );
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Obter uma revisão específica por ID
   */
  async getReviewById(reviewId: string): Promise<InspectionReportReview> {
    const response = await inspectionClient.get<InspectionReportReview>(
      `/api/inspection-report-reviews/${reviewId}`
    );
    return response.data;
  }

  /**
   * Verificar se um laudo tem correções pendentes
   */
  async hasPendingCorrections(reportId: string): Promise<boolean> {
    const response = await inspectionClient.get<boolean>(
      `/api/inspection-report-reviews/report/${reportId}/has-pending-corrections`
    );
    return response.data;
  }

  /**
   * Banco encaminha devolução externa para prestadora corrigir (status 21 → 16)
   */
  async forwardReturnToProvider(inspectionItemId: string): Promise<void> {
    await inspectionClient.post(
      `/api/inspection-report-returns/inspection-item/${inspectionItemId}/forward-to-provider`
    );
  }

  /**
   * Obter dados da devolução externa de um item de inspeção (último registro, resolvido ou não).
   * Necessário para que a prestadora visualize o motivo da devolução mesmo após o banco
   * encaminhar (que marca o registro como resolved).
   */
  async getReportReturnByInspectionItemId(inspectionItemId: string): Promise<{
    id: string;
    reason: string;
    description: string | null;
    returnedAt: string;
  } | null> {
    try {
      const response = await inspectionClient.get(
        `/api/inspection-report-returns/inspection-item/${inspectionItemId}/latest`
      );
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }
  
  async returnAfterApproval(
    inspectionItemId: string,
    payload: { reason: string; description: string }
  ): Promise<void> {
    await inspectionClient.post(
      `/api/inspection-report-reviews/item/${inspectionItemId}/return-after-approval`,
      payload
    );
  }
}

export const inspectionReportReviewService = new InspectionReportReviewService();
